import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Badge } from '@abyss/web/ui/Badge';
import { Heading } from '@abyss/web/ui/Heading';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import { t } from 'i18next';
import find from 'lodash/find';
import React, { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { ChoosePCPAlert } from '../../../../common/Alert/ChoosePCPAlert';
import { CardWrapper } from '../../../../common/CardWrapper';
import { ChipsCategory } from '../../../../common/ChipsCategory';
import {
  CareCategories,
  Constants,
  ProviderTypes,
  ReverseCoverageTypesCodes,
} from '../../../../common/Constants';
import { FeatureFlags } from '../../../../common/ConstantsFeatureFlags';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { mobileOnly, phoneOnly } from '../../../../common/ConstantsStyles';
import { DisclaimerContent } from '../../../../common/DisclaimerContent';
import { EAPCode } from '../../../../common/EAPCode';
import { EAPMixResultsContainer } from '../../../../common/EAPCode/EAPCode.styled';
import {
  getFilteredChips,
  isMNRHbpbCodePresent,
  validateChipsToTabEnabled,
} from '../../../../common/PSXHeader/SearchBar/utils';
import { ResultSection } from '../../../../common/ResultSection/ResultSection';
import {
  HeaderWrapper,
  HeadingContainer,
  ResultWrapper,
  headerTextStyle,
} from '../../../../common/ResultSection/ResultSection.style';
import { ResultSectionVirtual } from '../../../../common/ResultSection/ResultSectionVirtual';
import { getFeatureFlag } from '../../../../common/Utils';
import { NonUhcVisionPage } from '../../../../common/VisionCare/NonUhcVisionPage';
import { useCoverageType } from '../../../../hooks/useCoverageType';
import { useCurrentMemberIsPreEffective } from '../../../../hooks/useCurrentMemberIsPreEffective';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useTier1PlanIdentifier } from '../../../../hooks/useTier1PlanIdentifier';
import { Option } from '../../../../models/ProviderSearch';
import { useCeOffersStore } from '../../../../store/useCeOffersStore';
import { useTypeaheadStore } from '../../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../../store/useTypeaheadStore/typeaheadStore';
import { useVirtualCareStore } from '../../../../store/useVirtualCareStore';
import { returnSuppressFlag } from '../../../../utils/featureSuppress';
import { getLagoonConfigValue } from '../../../../utils/providerSearch.utils';
import { getCurrentMember } from '../../../../utils/user.utils';
import { VirtualCareList } from '../MapView/VirtualCareList';
import ProgramPromotionResults from '../ProgramPromotionSection/ProgramPromotionResults';
import { HealthGradesContainerStyled } from '../PSXHome/HealthGradesContainer.styled';
import { MedicalVisionInfographic } from './MedicalVisionInfographic';
import { SearchResultsDynamicTitle } from './SearchResultsDynamicTitle';
import { SearchResultsTitle } from './SearchResultsTitle';
import VirtualCareProviderSection from './VirtualCareProviderSection';

type Props = {
  acceptingNewPatients?: boolean;
  choosePCP: boolean;
  contextValue: string;
  data;
  dependentSeqNbr: string;
  eapCodeFlag: boolean;
  facilitiesHeaders;
  isMixedSuggestions: boolean;
  isPageTracked: boolean;
  isNameSearchEnabled?: boolean;
  isSpecialtySearchEnabled?: boolean;
  medGrpIsLoading: boolean;
  medicalGroupHeaders;
  onSpecialtyResultClicked(isSpecialtySearch: boolean, title: string): void;
  orgIsLoading: boolean;
  preferredFacility: string[];
  providerHeaders;
  providerIsLoading: boolean;
  resultType: string;
  searchMethod: string;
  searchTerm: string;
  searchTitle: string;
  searchType: string;
  showFacilities: boolean;
  showProviders: boolean;
  showProviderGroups: boolean;
  specialtyCode: string;
  specialtySearch: boolean;
  userZip: string;
  virtualCare: boolean;
  hasOnlyPseudoRollupCodes: boolean;
  invokeLlmModel?: boolean;
  areaOfExpertise?: Option[];
  specialtyCodeFromQuery: Option[];
  genderCode: Option[];
  virtualCareCode?: Option;
  preSelectEapFilter?: boolean;
  isMixedResultsPage?: boolean;
  categoryCount?: { chipEnabled: boolean; chipCategory: string }[];
  showChips?: boolean;
  specialtyResults?: { chipEnabled: boolean; chipCategory: string }[];
  essentialCommunityProviderCode?: Option[];
  virtualCareCardShowFlag?: boolean;
  includeSpecialityRollupCodes: string;
  translatedPsxKeyword?: string;
};

const containerStyles = (showMedicalVisionInfographic: boolean) => ({
  paddingBottom: showMedicalVisionInfographic ? '16px' : '0',
  paddingTop: showMedicalVisionInfographic ? '8px' : '0',
  width: '104.5%',
});

export const SearchedResultSectionData = ({
  acceptingNewPatients,
  dependentSeqNbr,
  preferredFacility,
  providerHeaders,
  medicalGroupHeaders,
  facilitiesHeaders,
  searchTitle,
  searchMethod,
  searchTerm,
  searchType,
  contextValue,
  specialtyCode,
  userZip,
  choosePCP,
  isMixedSuggestions,
  isNameSearchEnabled,
  isSpecialtySearchEnabled,
  onSpecialtyResultClicked,
  resultType,
  specialtySearch,
  data,
  eapCodeFlag,
  providerIsLoading,
  medGrpIsLoading,
  orgIsLoading,
  isPageTracked,
  showFacilities,
  showProviders,
  showProviderGroups,
  virtualCare,
  hasOnlyPseudoRollupCodes,
  invokeLlmModel,
  areaOfExpertise,
  specialtyCodeFromQuery,
  genderCode,
  virtualCareCode,
  preSelectEapFilter = false,
  isMixedResultsPage = false,
  categoryCount = [],
  showChips,
  specialtyResults = [],
  essentialCommunityProviderCode,
  virtualCareCardShowFlag,
  includeSpecialityRollupCodes = '',
  translatedPsxKeyword = '',
}: Props) => {
  const coverageType = useCoverageType();
  const [isVirtualCareListView, setIsVirtualCareListView] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('virtualCareListViewPage') === 'true';
  });
  useEffect(() => {
    const handleUrlChange = () => {
      const params = new URLSearchParams(window.location.search);
      setIsVirtualCareListView(
        params.get('virtualCareListViewPage') === 'true'
      );
    };
    window.addEventListener('popstate', handleUrlChange);
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);
  useTier1PlanIdentifier(data?.isTieredPlan);
  const [enableMapViewEnhancements, enableVirtualCareMixedSearchResult] =
    useFeatureFlag([
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUALCARE_MIXED_SEARCH_RESULT,
    ]);
  const mobileScreen = useMediaQuery(
    enableMapViewEnhancements ? mobileOnly : phoneOnly
  );
  const uiMessaging = useLagoon('ui-messaging')();
  const virtualVisitCostLabel = find(uiMessaging, {
    key: ConstantsLagoon.VIRTUAL_VISIT.VIRTUAL_VISIT_COST,
  });
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const chipsToTabsEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.CARE_CATEGORY_CHIPS_TO_TABS_ENABLED
  );
  const virtualVisitViewAll = find(uiMessaging, {
    key: ConstantsLagoon.VIRTUAL_VISIT.VIRTUAL_VISIT_VIEW_ALL,
  });
  const virtualVisitOnDemandOrScheduledAppointmentsText = find(uiMessaging, {
    key: ConstantsLagoon.VIRTUAL_VISIT
      .ONDEMAND_SCHEDULED_APPOINTMENTS_AVAILABLE,
  });
  const isPreEffective = useCurrentMemberIsPreEffective();
  const virtualVisitChipEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_VISIT
  );
  const showVirtualCare = getFeatureFlag(
    featureFlags,
    FeatureFlags.FEATURE_FLAG_KEY_CHIP_MAP[Constants.CHIPS_CATEGORIES.ALL]
      .VIRTUAL_CARE
  );
  const suppressVirtualCareQuickCare = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE
  );
  const enableMedicalVisionIdentifier = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MEDICAL_VISION_IDENTIFIER
  );
  const isVirtualCareSuppressedForEmpire = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_EMPIRE
  );
  const enableVirtualVisitSection =
    virtualVisitChipEnabled &&
    virtualCareCardShowFlag &&
    showVirtualCare &&
    !isPreEffective &&
    !suppressVirtualCareQuickCare;
  const visionChipEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.VISION_CHIP_ENABLED
  );
  const dentalChipEnabled: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.DENTAL_CHIP_ENABLED
  );
  const enableMixedResultPageUpdatedDesign: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MIXED_RESULT_PAGE_UPDATED_DESIGN
  );
  const changeRCLogicForVC: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.CHANGE_ROLLUP_CODE_LOGIC_VC
  );
  const enableProgramPromotion: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PROGRAM_PROMOTION
  );
  const programPromotionsForResultPage = useCeOffersStore(
    (state) => state.programPromotionsForResultPage
  );
  const currentMember = getCurrentMember();
  const filteredChips = getFilteredChips(
    currentMember,
    isMixedResultsPage,
    categoryCount,
    visionChipEnabled,
    dentalChipEnabled,
    isVirtualCareSuppressedForEmpire,
    enableVirtualVisitSection
  );
  const chipCount = filteredChips.filter((chip) => chip.enabled).length;

  const chipsEnabled = validateChipsToTabEnabled(
    chipsToTabsEnabled,
    isMixedResultsPage
  );

  const isBHCare: boolean = coverageType?.includes(
    ReverseCoverageTypesCodes['BEHAVIORAL HEALTH']
  );

  const allConfigs = useLagoon('config');
  const hbpbCodes =
    getLagoonConfigValue(allConfigs, 'VISION_NON_UHC_ICARE')?.split(',') || [];
  const vcEnabledChips = getLagoonConfigValue(
    allConfigs,
    'VIRTUAL_CARE_ENABLED_CHIPS'
  )
    ?.split(',')
    .map((v) => v.trim());

  const vcRollupCodes = getLagoonConfigValue(
    allConfigs,
    'VIRTUAL_CARE_ROLLUP_CODES'
  )
    ?.split(',')
    .map((v) => v.trim());

  const vcRollupCodesV2 = getLagoonConfigValue(
    allConfigs,
    'VIRTUAL_CARE_ROLLUP_CODES_V2'
  )
    ?.split(',')
    .map((v) => v.trim());

  const enableProgramPromotionInResultPage = getLagoonConfigValue(
    allConfigs,
    'ENABLE_PROGRAM_PROMOTION_IN_RESULT_PAGE'
  );

  const includeSpecialityRollupCodesArray =
    includeSpecialityRollupCodes?.split(',');

  const virtualCareProviders = useVirtualCareStore(
    (state) => state.virtualCareProviders
  );

  const ResultsSectionMain = (
    res,
    secType,
    count,
    sectionProviderType,
    radius,
    isLoading,
    headers
  ) => (
    <ResultSection
      acceptingNewPatients={acceptingNewPatients}
      areaOfExpertise={areaOfExpertise}
      dependentSeqNbr={dependentSeqNbr}
      essentialCommunityProviderCode={essentialCommunityProviderCode}
      genderCode={genderCode}
      headers={headers}
      invokeLlmModel={invokeLlmModel}
      isLoading={isLoading}
      isTieredPlan={data?.isTieredPlan}
      preSelectEapFilter={preSelectEapFilter}
      preferredFacility={preferredFacility}
      providerType={sectionProviderType}
      results={res}
      search={searchTitle}
      searchMethod={searchMethod}
      searchRadius={radius}
      searchTerm={searchTerm}
      searchType={searchType}
      sectionType={secType}
      selectedChip={contextValue}
      specialtyCode={specialtyCode}
      specialtyCodeFromQuery={specialtyCodeFromQuery}
      totalCount={count}
      userZip={userZip}
      virtualCare={virtualCare}
      virtualCareCode={virtualCareCode}
    />
  );

  function hasAllAllowedRollupCodesVC(): boolean {
    if (changeRCLogicForVC) {
      return includeSpecialityRollupCodesArray.some((vcr) =>
        vcRollupCodesV2.includes(vcr?.trim())
      );
    }
    return includeSpecialityRollupCodesArray.every((vcr) =>
      vcRollupCodes.includes(vcr?.trim())
    );
  }

  const visionItems = useLagoon('vision-non-uhc')();
  const isNonUhcmember = isMNRHbpbCodePresent(currentMember, hbpbCodes);
  const hasVisionChip = filteredChips.some(
    (chip) => chip.value === CareCategories.VISION && chip.enabled
  );
  const hasMedicalSpecialistsChip = filteredChips.some(
    (chip) => chip.value === CareCategories.MEDICAL_SPECIALISTS && chip.enabled
  );
  const showMedicalVisionInfographic =
    enableMedicalVisionIdentifier &&
    showChips &&
    chipsEnabled &&
    enableMixedResultPageUpdatedDesign &&
    hasVisionChip &&
    hasMedicalSpecialistsChip;

  const { setTypeaheadSearchStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      setTypeaheadSearchStore: state.setTypeaheadSearchStore,
    }))
  );

  useEffect(() => {
    setTypeaheadSearchStore({
      isVisionRelatedSearch: showMedicalVisionInfographic,
    });
  }, [showMedicalVisionInfographic]);

  const ResultsSectionHeaderLayoutComponent = mobileScreen
    ? Layout.Stack
    : Layout.Group;

  return (
    <React.Fragment>
      {!isVirtualCareListView ? (
        <CardWrapper
          style={{
            '@screen < $md': {
              marginLeft: '0',
            },
          }}
        >
          <Layout.Stack grow space={0}>
            {!enableMapViewEnhancements && <ChoosePCPAlert />}
            <ResultsSectionHeaderLayoutComponent
              alignItems={mobileScreen ? 'left' : 'top'}
              alignLayout={mobileScreen ? 'center' : 'left'}
              css={containerStyles(showMedicalVisionInfographic)}
              grow
              space={24}
            >
              <Layout.Stack alignItems="left" grow space={0}>
                {enableMixedResultPageUpdatedDesign ? (
                  <SearchResultsDynamicTitle
                    chipCount={chipCount}
                    chips={filteredChips}
                    choosePCP={choosePCP}
                    hasOnlyPseudoRollupCodes={hasOnlyPseudoRollupCodes}
                    isMixedSuggestions={isMixedSuggestions}
                    isNameSearchEnabled={isNameSearchEnabled}
                    isSpecialtySearchEnabled={isSpecialtySearchEnabled}
                    offset={1}
                    onSpecialtyClick={onSpecialtyResultClicked}
                    searchTitle={searchTitle}
                    specialtyResults={specialtyResults}
                    specialtySearch={specialtySearch}
                    translatedPsxKeyword={translatedPsxKeyword}
                  />
                ) : (
                  <SearchResultsTitle
                    choosePCP={choosePCP}
                    hasOnlyPseudoRollupCodes={hasOnlyPseudoRollupCodes}
                    isMixedSuggestions={isMixedSuggestions}
                    isNameSearchEnabled={isNameSearchEnabled}
                    isSpecialtySearchEnabled={isSpecialtySearchEnabled}
                    offset={1}
                    onSpecialtyClick={onSpecialtyResultClicked}
                    resultType={resultType}
                    searchTitle={searchTitle}
                    specialtyResults={specialtyResults}
                    specialtySearch={specialtySearch}
                  />
                )}
                {showChips &&
                  chipsEnabled &&
                  enableMixedResultPageUpdatedDesign && (
                    <Layout.Stack grow space={0}>
                      <ChipsCategory
                        chips={filteredChips}
                        data-auto-testid="search-home-chips-category"
                        data-testid="search-home-chips-category"
                        isMixedResultsPage={isMixedResultsPage}
                        resultSection={true}
                      />
                    </Layout.Stack>
                  )}
              </Layout.Stack>
              {showMedicalVisionInfographic && (
                <MedicalVisionInfographic isMobile={mobileScreen} />
              )}
            </ResultsSectionHeaderLayoutComponent>
            {enableVirtualCareMixedSearchResult &&
              virtualCareProviders &&
              Object?.keys(virtualCareProviders)?.length > 0 && (
                <VirtualCareProviderSection
                  headerTextStyle={headerTextStyle}
                  setIsVirtualCareListView={setIsVirtualCareListView}
                  virtualCareProviders={virtualCareProviders}
                  virtualVisitViewAll={t('Virtual Care results')}
                />
              )}
            {enableVirtualVisitSection &&
            vcEnabledChips.includes(contextValue) &&
            hasAllAllowedRollupCodesVC() ? (
              <div style={{ paddingBottom: '48px' }}>
                <HeaderWrapper>
                  <ResultWrapper>
                    <HeadingContainer>
                      <Heading
                        css={headerTextStyle}
                        data-testid="virtual-visit-heading"
                        display="h6"
                        offset={2}
                      >
                        <div style={{ display: 'flex', gap: '12px' }}>
                          {virtualVisitViewAll?.title}{' '}
                          <Badge outline>
                            {virtualVisitCostLabel?.message}
                          </Badge>
                        </div>
                      </Heading>
                    </HeadingContainer>
                  </ResultWrapper>
                </HeaderWrapper>
                <ResultSectionVirtual showCarouselInMobile={true} />
                <Text>
                  {virtualVisitOnDemandOrScheduledAppointmentsText?.message}
                </Text>
              </div>
            ) : null}
            {!enableMapViewEnhancements &&
              contextValue === CareCategories.BEHAVIORAL_HEALTH &&
              eapCodeFlag &&
              isBHCare &&
              isPageTracked && (
                <EAPMixResultsContainer>
                  <EAPCode />
                </EAPMixResultsContainer>
              )}
            {contextValue === CareCategories.VISION && isNonUhcmember ? (
              <NonUhcVisionPage
                NonUhcVisionContent={visionItems}
                isPsxHome={false}
              />
            ) : (
              <>
                {contextValue !== CareCategories.FACILITIES &&
                contextValue !== CareCategories.HEALTH_SUPPLIES &&
                showProviders
                  ? ResultsSectionMain(
                      data?.providers,
                      Constants.SEARCH_TYPES.PROVIDER,
                      data?.totalCountPractitioners,
                      ProviderTypes.PRACTITIONER,
                      data?.practitionerSearchRadius,
                      providerIsLoading,
                      providerHeaders
                    )
                  : null}
                {contextValue !== CareCategories.FACILITIES &&
                contextValue !== CareCategories.HEALTH_SUPPLIES &&
                showProviderGroups
                  ? ResultsSectionMain(
                      data?.medicalGroup,
                      Constants.SEARCH_TYPES.PROVIDER_GROUP,
                      data?.totalCountMedicalGroup,
                      ProviderTypes.MEDICAL_GROUP,
                      data?.medicalGroupSearchRadius,
                      medGrpIsLoading,
                      medicalGroupHeaders
                    )
                  : null}
                {!choosePCP && showFacilities
                  ? ResultsSectionMain(
                      data?.facilities,
                      Constants.SEARCH_TYPES.FACILITY,
                      data?.totalCountOrganizations,
                      ProviderTypes.ORGANIZATION,
                      data?.organizationSearchRadius,
                      orgIsLoading,
                      facilitiesHeaders
                    )
                  : null}
                {enableProgramPromotion &&
                  enableProgramPromotionInResultPage === 'Y' &&
                  programPromotionsForResultPage.length > 0 && (
                    <ProgramPromotionResults
                      isMixedResultsPage={isMixedResultsPage}
                      programPromotionsForResultPage={
                        programPromotionsForResultPage
                      }
                      searchTerm={searchTerm}
                    />
                  )}
              </>
            )}
          </Layout.Stack>
        </CardWrapper>
      ) : (
        <VirtualCareList
          isLoading={false}
          mobileScreen={mobileScreen}
          searchTerm={searchType}
          sectionType={''}
          serviceType={virtualCareProviders?.[0]?.serviceType}
          setIsVirtualCareListView={setIsVirtualCareListView}
          virtualCareProviders={virtualCareProviders}
        />
      )}
      <HealthGradesContainerStyled>
        <DisclaimerContent selectedChipValue={contextValue} />
      </HealthGradesContainerStyled>
    </React.Fragment>
  );
};
